/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import mbpLogger from 'mbp-logger';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    string, array, object,
} from 'prop-types';
import ReactMarkdown from 'react-markdown';
import {
    Button,
    FormControl, FormHelperText, Input, Select, Checkbox, InputLabel,
} from '@material-ui/core';
import Joi from 'joi-browser';
import mbpUtil from 'mbp-api-util';
import ReCAPTCHA from 'react-google-recaptcha';
import EventDispatcher from '../../../../../pages/Account/state/reducers/EventDispatcher';
import GraphqlSeoHeadTags from '../../../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { isEmpty } from '../../../../../pages/Account/utils/object';
import States from '../../../../../pages/Checkout/constants/states';
import { hideLabelWithAssistiveTech } from '../../../../../helpers/common/helper';
import * as auth from '../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';

const APP_GOOGLE_RECAPCHA_KEY = mbpUtil.getEnv('APP_GOOGLE_RECAPCHA_KEY') ? mbpUtil.getEnv('APP_GOOGLE_RECAPCHA_KEY') : '6Lc9wiEUAAAAAFqvKqm7Etmli7bL5rT_HscAOBAU';
const callBackUrl = '/marketplace-diversity-inclusion-program-attestation-form';

const useStyles = makeStyles((theme) => ({

    formContainer: () => ({
        width: '100%',
        margin: '30px auto 0',
        backgroundColor: '#EFEBF3',
        [theme.breakpoints.down(600)]: {
            maxWidth: '600px',
            margin: '0 auto',
            padding: '20px 0 30px 0',
        },
    }),
    formWrapper: {
        maxWidth: '950px',
        padding: '3% 0 8% 0',
        display: 'flex',
        margin: '0 auto',
        flexDirection: 'column',
        [theme.breakpoints.down(914)]: {
            maxWidth: '90%',
            display: 'block',
            padding: '3% 1rem 8% 1rem',
        },
    },

    formHeaderContainer: {
        flex: '0 0 30%',
        paddingRight: '5%',
        textAlign: 'left',
        [theme.breakpoints.down(600)]: {
            padding: '0 0 15px',
        },
    },
    formTitle: {
        fontFamily: 'PlayfairDisplayBold, serif',
        fontSize: '2em',
        marginBottom: '15px',
    },
    formDescription: {
        fontFamily: 'latolight, serif',
        fontSize: '1em',
    },

    formFieldsContainer: {
        flex: '0 0 70%',
        textAlign: 'left',
        '& .Mui-disabled': {
            backgroundColor: theme.palette.grey?.['300'] || '#E0E0E0',
        },
    },
    formFields: {
        width: '100%',
    },

    formControl: {
        width: '100%',
        '& input': {
            border: 'none',
            minHeight: '35px',
            backgroundColor: theme.palette.cms?.white || '#ffffff',
            padding: '0',
        },
        [theme.breakpoints.down(600)]: {
            minHeight: '50px',
        },
    },
    singleSelectField: {
        backgroundColor: theme.palette.cms?.white || '#ffffff',
        '& .MuiSelect-select:focus': {
            backgroundColor: theme.palette.cms?.white || '#ffffff',
        },
        width: '30%',
        [theme.breakpoints.down(600)]: {
            width: '100%',
        },
    },
    multiSelectField: {
        backgroundColor: theme.palette.cms?.white || '#ffffff',
        '& .MuiSelect-select:focus': {
            backgroundColor: theme.palette.cms?.white || '#ffffff',
        },
    },
    menuItem: {
        whiteSpace: 'normal',
    },
    multiSelectLabelHelperText: {
        fontSize: '14px',
        fontFamily: 'latolight, serif',
    },
    fieldsLabel: {
        fontFamily: 'latolight, serif',
        margin: '13px auto 5px 0',
        '& p': {
            margin: '0 auto',
        },
        '& > div': {
            backgroundColor: theme.palette.cms?.white || '#ffffff',
        },
    },
    ctaButton: {
        width: '15%',
        backgroundColor: theme.palette.cms?.black || '#000000',
        color: theme.palette.cms?.white || '#ffffff',
        margin: '20px auto 0',
        letterSpacing: '1px',
        borderRadius: '0',
        '&:disabled': {
            backgroundColor: `${theme.palette.colorNeutral40} !important`,
        },
        [theme.breakpoints.down(600)]: {
            width: '100%',
            margin: '30px auto 0',
        },
        '&:hover': {
            backgroundColor: theme.palette.cms?.black || '#000000',
        },
    },
    results: {
        width: '100%',
        margin: '0 auto 0 0',
    },
    failedSubmission: {
        color: theme.palette.colorAlert,
    },
    successfulSubmission: {
        textTransform: 'capitalize',
        color: theme.palette.colorSuccess,
    },
    ariaInvisible: () => hideLabelWithAssistiveTech(),
    recaptcha: {
        margin: '13px auto 5px 0',
    },
}));

const getFormValidationSchema = (formFields, isSellersFormCaptchaEnabled) => {
    const schema = {};
    formFields?.forEach((field) => {
        const { field_key, field_label, field_type } = field.field;
        if (field_key === 'email') {
            schema[field_key] = Joi.string()
                .trim()
                .max(50)
                .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/i, 'email')
                .required()
                .label(field_label);
        } else if (field_key === 'phone_number' || field_key === 'mobile_number') {
            schema[field_key] = Joi.string()
                .trim()
                .regex(/^\d{3}-?\d{3}-?\d{4}$|^\d{10}$/, 'number')
                .required()
                .label(field_label);
        } else if (field_key === 'first_name' || field_key === 'last_name' || field_key === 'seller_name') {
            schema[field_key] = Joi.string()
                .min(3)
                .max(50)
                .trim()
                .required()
                .label(field_label);
        } else if (field_type === 'Text') {
            schema[field_key] = Joi.string()
                .max(50)
                .trim()
                .required()
                .label(field_label);
        } else if (field_type === 'Single choice') {
            schema[field_key] = Joi.string()
                .trim()
                .required()
                .label(field_label);
        } else if (field_type === 'Multiple choice') {
            schema[field_key] = Joi.array().min(1)
                .required()
                .label(field_label);
        } else if (field_type === 'Boolean') {
            schema[field_key] = Joi.boolean()
                .valid(true)
                .required(true)
                .label(field_label);
        }
    });
    if (isSellersFormCaptchaEnabled) {
        schema.recaptcha = Joi.string().required().label('Recaptcha');
    }
    return schema;
};

const getFormInitState = (formFields, isSellersFormCaptchaEnabled) => {
    const initState = {};
    formFields?.forEach((field) => {
        const { field_key, field_type } = field.field;
        if (field_type === 'Text' || field_type === 'Single choice') {
            initState[field_key] = '';
        } else if (field_type === 'Multiple choice') {
            initState[field_key] = [];
        } else if (field_type === 'Boolean') {
            initState[field_key] = false;
        }
    });
    if (isSellersFormCaptchaEnabled) {
        initState.recaptcha = null;
    }
    return initState;
};

const getHostNamePrefix = () => {
    const env = mbpUtil.getEnv('APP_GRAPHQL_ENV');
    switch (env) {
        case 'production':
            return 'api.18f.tech';
        case 'uat1':
            return 'uat1-api.18f.tech';
        case 'development':
            return 'dev1-api.18f.tech';
        default:
            return '';
    }
};

const getLocalPagesCsrfApi = async (headers, hostNamePrefix) => {
    try {
        const endpoint = `https://${hostNamePrefix}/r/api/growth/grw-localpages-be/getCSRFToken`;
        const csrfApiResponse = await fetch(endpoint, {
            method: 'get',
            headers,
            credentials: 'include',
            mode: 'cors',
        });
        if (!csrfApiResponse.ok) {
            throw new Error(`HTTP error! status: ${csrfApiResponse.status}`);
        }
        return await csrfApiResponse.json();
    } catch (err) {
        const message = `error fetching local pages csrf api: ${err}`;
        mbpLogger.logError({
            function: 'Form.getLocalPagesCsrfApi',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message,
        });
        return null;
    }
};

const getHeaders = async () => {
    const accessToken = await auth?.getAccessTokenSafely();
    const headers = {
        'content-type': 'application/json',
        accept: 'application/json',
        authorization: `Bearer ${accessToken}`,
    };
    return headers;
};

const Form = ({
    form_fields, description_copy_html, disclaimer_copy, title, seo,
}) => {
    const isSellersFormCaptchaEnabled = useSelector(getFeatureFlag('is-self-attested-form-captcha-enabled'));
    const [formData, setFormData] = useState(
        getFormInitState(form_fields, isSellersFormCaptchaEnabled),
    );
    const schema = getFormValidationSchema(form_fields, isSellersFormCaptchaEnabled);

    const [errors, setErrors] = useState({});
    const [formSubmitResponse, setFormSubmitResponse] = useState({
        isSubmitted: false,
        responseMessage: '',
    });
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
    const dispatch = useDispatch();
    const validateCaptcha = (value, callBackURL) => dispatch(EventDispatcher.validateCaptcha(value, callBackURL));

    const getErrorMsg = (fieldName, error) => {
        if (fieldName === 'email') {
            return 'Please enter valid "Email"';
        }
        if (fieldName === 'phone_number' || fieldName === 'mobile_number') {
            return 'Please enter valid "Number"';
        }
        if (fieldName === 'opt_in') {
            return 'Please check "Opt-in" field';
        }
        if (fieldName === 'recaptcha') {
            return 'Please complete the Captcha to proceed.';
        }
        return error.details?.[0]?.message;
    };
    const validateProperty = ({ name, value }) => {
        if (!name) return null;
        const obj = { [name]: value };
        const localSchema = { [name]: schema[name] };
        const { error } = Joi.validate(obj, localSchema);
        return error && getErrorMsg(name, error);
    };
    const handleChangeRecap = (value) => {
        // validate captcha token server side
        try {
            validateCaptcha(value, callBackUrl);
        } catch (err) {
            const message = `error in validating google recaptcha ${err}`;
            mbpLogger.logError({
                function: 'Form.handleChangeRecap',
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                message,
            });
        }
        const modifiedFieldInput = {};
        modifiedFieldInput.name = 'recaptcha';
        modifiedFieldInput.value = value;

        const modifiedFieldErrors = { ...errors };
        const errorMessage = validateProperty(modifiedFieldInput);
        if (errorMessage) {
            modifiedFieldErrors[modifiedFieldInput.name] = errorMessage;
        } else {
            delete modifiedFieldErrors[modifiedFieldInput.name];
        }
        setErrors(modifiedFieldErrors);
        setFormData((prevState) => ({ ...prevState, ...{ [modifiedFieldInput.name]: value } }));
    };
    const handleFieldUpdate = (fieldKey, value) => {
        const modifiedFieldInput = {};
        modifiedFieldInput.name = fieldKey;
        modifiedFieldInput.value = value;

        const modifiedFieldErrors = { ...errors };
        const errorMessage = validateProperty(modifiedFieldInput);
        if (errorMessage) {
            modifiedFieldErrors[modifiedFieldInput.name] = errorMessage;
        } else {
            delete modifiedFieldErrors[modifiedFieldInput.name];
        }
        setErrors(modifiedFieldErrors);
        setFormData((prevState) => ({ ...prevState, ...{ [fieldKey]: value } }));
    };
    const getMultipleSelectValue = (event) => {
        const { options } = event?.target;
        const multiSelectValue = [];
        for (let i = 0; i < options?.length; i += 1) {
            if (options[i]?.selected) {
                multiSelectValue.push(options[i]?.value);
            }
        }
        return multiSelectValue;
    };

    const isRequiredFieldsMissing = () => {
        let hasMissingFieldsStatus = false;
        for (let i = 0; i < form_fields?.length; i += 1) {
            const { field_key, field_type } = form_fields[i]?.field;
            const fieldValue = formData[field_key];
            if ((field_type === 'Boolean' && fieldValue === false) || fieldValue?.length === 0) {
                hasMissingFieldsStatus = true;
                const modifiedError = {};
                modifiedError[field_key] = 'This field is required';
                setErrors((prevState) => ({
                    ...prevState,
                    ...modifiedError,
                }));
            }
        }
        return hasMissingFieldsStatus;
    };
    const updateSelfAttestationMarketplaceFormData = async (data) => {
        const headers = await getHeaders();
        const {
            seller_name, first_name, last_name, city,
            state, phone_number, mobile_number,
            email, opt_in, my_business_identifies_as,
        } = data;
        const hostNamePrefix = getHostNamePrefix();
        const csrfApiResponse = await getLocalPagesCsrfApi(headers, hostNamePrefix);
        let selfAttestedFormResponse;
        try {
            const endpoint = `https://${hostNamePrefix}/r/api/growth/grw-localpages-be/admin/selfAttestation/addDetails`;
            const requestBody = {
                sellerName: seller_name?.trim(),
                firstName: first_name?.trim(),
                lastName: last_name?.trim(),
                city: city?.trim(),
                state: state?.trim(),
                phoneNumber: phone_number?.trim(),
                mobileNumber: mobile_number?.trim(),
                email: email?.trim(),
                businessIdentifiesAs: my_business_identifies_as,
                optIn: opt_in,
                XSRF_TOKEN: csrfApiResponse?.XSRF_TOKEN,
            };
            const response = await fetch(endpoint, {
                method: 'post',
                headers,
                mode: 'cors',
                cache: 'no-cache',
                credentials: 'include',
                body: JSON.stringify(requestBody),
            });
            if (!response.ok) {
                const error = new Error(`HTTP error! status: ${response.status}`);
                error.code = response.status;
                throw error;
            }
            const jsonResponse = await response.json();
            jsonResponse.message = 'Successfully Submitted!';
            selfAttestedFormResponse = { isSubmitted: true, responseMessage: jsonResponse.message };
        } catch (err) {
            const message = `error fetching self attestation market place form status: ${err}`;
            mbpLogger.logError({
                function: 'Form.updateSelfAttestationMarketplaceFormData',
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                message,
            });
            const responseMessage = 'Error! something went wrong, please try again later.';
            selfAttestedFormResponse = { isSubmitted: false, responseMessage };
            setIsSubmitButtonDisabled(false);
        }
        return selfAttestedFormResponse;
    };
    const submitSelfAttestationForm = async (e) => {
        e?.preventDefault();
        if (isRequiredFieldsMissing()) return;
        if (!isEmpty(errors)) return;
        if (isEmpty(errors) && formData['recaptcha'] === null) {
            setErrors((prevState) => ({
                ...prevState,
                recaptcha: 'Please complete the Captcha to proceed.',
            }));
            return;
        }
        await setIsSubmitButtonDisabled(true);
        const submitReponseStatus = await updateSelfAttestationMarketplaceFormData(formData);
        setFormSubmitResponse(submitReponseStatus);
    };

    const classes = useStyles();
    const getFormField = (field) => {
        const {
            field_key,
            field_label,
            field_type,
            field_choices,
        } = field.field;
        if (field_type === 'Text') {
            return (
                <>
                    <FormControl className={classes.formControl} autoComplete="nope">
                        <ReactMarkdown className={classes.fieldsLabel} source={`${field_label}*`} escapeHtml={false} />
                        <InputLabel
                            htmlFor={field_key}
                            className={classes.ariaInvisible}
                            shrink
                        >
                            {field_key}
                        </InputLabel>
                        <Input
                            disabled={formSubmitResponse.isSubmitted}
                            disableUnderline
                            autoComplete="nope"
                            id={field_key}
                            name={field_key}
                            type="text"
                            value={formData[field_key]}
                            onChange={(e) => handleFieldUpdate(`${field_key}`, e.target.value)}
                        />
                        {errors[field_key] ? (
                            <FormHelperText className={`form-helper-text ${errors[field_key] ? 'Mui-error' : ''}`}>{errors[field_key]}</FormHelperText>
                        ) : (
                            ''
                        )}
                    </FormControl>
                </>
            );
        }
        if (field_type === 'Single choice') {
            let options = field_choices?.split(',');
            if (field_key === 'state') {
                options = Object.keys(States.US);
            }
            return (
                <>
                    <FormControl className={classes.formControl} autoComplete="nope">
                        <ReactMarkdown className={classes.fieldsLabel} source={`${field_label}*`} escapeHtml={false} />
                        <InputLabel
                            htmlFor={field_key}
                            className={classes.ariaInvisible}
                            shrink
                        >
                            {field_key}
                        </InputLabel>
                        <Select
                            className={classes.singleSelectField}
                            disabled={formSubmitResponse.isSubmitted}
                            disableUnderline
                            native
                            autoComplete="nope"
                            id={field_key}
                            name={field_key}
                            value={formData[field_key]}
                            onChange={(e) => handleFieldUpdate(`${field_key}`, e.target.value)}
                        >
                            <option
                                key={`nativeSelect${field_label}`}
                                value=""
                                className={classes.menuItem}
                            >
                                Select {`${field_label}`}
                            </option>
                            {options.map((optionKey) => (
                                <option
                                    key={optionKey}
                                    value={field_key === 'state' ? States.US[optionKey] : optionKey}
                                    className={classes.menuItem}
                                >
                                    {field_key === 'state' ? States.US[optionKey] : optionKey}
                                </option>
                            ))}
                        </Select>
                        {errors[field_key] ? (
                            <FormHelperText className={`form-helper-text ${errors[field_key] ? 'Mui-error' : ''}`}>{errors[field_key]}</FormHelperText>
                        ) : (
                            ''
                        )}
                    </FormControl>
                </>
            );
        }
        if (field_type === 'Multiple choice') {
            if (field_key === 'my_business_identifies_as') {
                return (
                    <>
                        <FormControl className={classes.formControl} autoComplete="nope">
                            <ReactMarkdown className={classes.fieldsLabel} source={`${field_label}*`} escapeHtml={false} />
                            <InputLabel
                                htmlFor={field_key}
                                className={classes.ariaInvisible}
                                shrink
                            >
                                {field_key}
                            </InputLabel>

                            <Select
                                className={classes.multiSelectField}
                                native
                                multiple
                                disabled={formSubmitResponse.isSubmitted}
                                disableUnderline
                                autoComplete="nope"
                                id={field_key}
                                name={field_key}
                                value={formData[field_key]}
                                onChange={(e) => handleFieldUpdate(`${field_key}`, getMultipleSelectValue(e))}
                            >
                                {field_choices?.split(',').map((optionKey) => (
                                    <option
                                        key={optionKey}
                                        value={optionKey}
                                        className={classes.menuItem}
                                    >
                                        {optionKey}
                                    </option>
                                ))}
                            </Select>
                            <span className={classes.multiSelectLabelHelperText}>Select all that apply.</span>
                            {errors[field_key] ? (
                                <FormHelperText className={`form-helper-text ${errors[field_key] ? 'Mui-error' : ''}`}>{errors[field_key]}</FormHelperText>
                            ) : (
                                ''
                            )}
                        </FormControl>
                    </>
                );
            }
        }
        if (field_type === 'Boolean' && field_key === 'opt_in') {
            return (
                <>
                    <div>
                        <p>{disclaimer_copy}</p>
                    </div>
                    <FormControl className={classes.formControl} autoComplete="nope">
                        <div style={{ display: 'flex' }}>
                            <InputLabel
                                htmlFor={field_key}
                                className={classes.ariaInvisible}
                                shrink
                            >
                                {field_key}
                            </InputLabel>
                            <Checkbox
                                style={{ width: '30px', marginLeft: '-5px', color: '#65388b' }}
                                disabled={formSubmitResponse.isSubmitted}
                                autoComplete="nope"
                                id={field_key}
                                name={field_key}
                                value={formData[field_key]}
                                onChange={(e) => handleFieldUpdate(`${field_key}`, e.target.checked)}
                            />
                            <span style={{ display: 'flex', alignItems: 'center', fontFamily: 'latolight, serif' }}>{field_label}*</span>
                        </div>
                        {errors[field_key] ? (
                            <FormHelperText className={`form-helper-text ${errors[field_key] ? 'Mui-error' : ''}`}>{errors[field_key]}</FormHelperText>
                        ) : (
                            ''
                        )}
                    </FormControl>
                </>
            );
        }
        return null;
    };

    return (
        <>
            <GraphqlSeoHeadTags seoData={seo} />
            <div className={classes.formContainer}>
                <div className={classes.formWrapper}>
                    <div className={classes.formHeaderContainer}>
                        <div className={classes.formTitle}>{title}</div>
                        <ReactMarkdown className={classes.formDescription} source={description_copy_html} escapeHtml={false} />
                    </div>
                    <div className={classes.formFieldsContainer}>
                        <div className={classes.formFields}>
                            {form_fields?.map((field) => getFormField(field))}
                            <div>
                                {
                                    isSellersFormCaptchaEnabled && !formSubmitResponse.isSubmitted ? (
                                        <>
                                            <ReCAPTCHA
                                                sitekey={APP_GOOGLE_RECAPCHA_KEY}
                                                onChange={(value) => handleChangeRecap(value)}
                                                tabindex={0}
                                                size="normal"
                                                className={classes.recaptcha}
                                            />
                                            {errors['recaptcha'] ? (
                                                <FormHelperText className={`form-helper-text ${errors['recaptcha'] ? 'Mui-error' : ''}`}>{errors['recaptcha']}</FormHelperText>
                                            ) : (
                                                ''
                                            )}
                                        </>
                                    ) : null
                                }
                            </div>
                        </div>
                        <Button
                            disabled={isSubmitButtonDisabled}
                            className={classes.ctaButton}
                            type="submit"
                            onClick={submitSelfAttestationForm}
                        >
                            submit
                        </Button>

                        <div className={classes.results}>
                            {!isEmpty(errors) && (
                                <FormHelperText className="form-helper-text Mui-error">{errors['recaptcha'] ? '' : 'Please fill all required fields'}</FormHelperText>
                            )}
                            {formSubmitResponse.isSubmitted !== '' && (
                                <div className={(formSubmitResponse?.isSubmitted) ? classes.successfulSubmission : classes.failedSubmission}>
                                    <p>{formSubmitResponse?.responseMessage}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </>
    );
};

Form.propTypes = {
    form_fields: array.isRequired,
    description_copy_html: string.isRequired,
    disclaimer_copy: string.isRequired,
    title: string.isRequired,
    seo: object.isRequired,
};

export default Form;
